import { documentationUnlocked, isRecordLocked } from "./display";
import { isLateEntry } from "./time";
import { hasText } from "./text";
import { Incident, IncidentDocumentation, NotificationParty } from "./types";

function missingNotification(incident: Incident, party: NotificationParty): boolean {
  return !(incident.notifications ?? []).some(
    (row) => row.party === party && row.done && row.at && hasText(row.person_name)
  );
}

function requireText(errors: string[], value: unknown, message: string): void {
  if (!hasText(value)) errors.push(message);
}

export function applyLateFlag(documentation: IncidentDocumentation, now: Date): IncidentDocumentation {
  const late = isLateEntry(documentation.event_time || documentation.discovered_at, now);
  return { ...documentation, late_entry: late };
}

/** Messages shown before finalize. Empty means the nurse may lock the record. */
export function validateFinalize(incident: Incident, now = new Date()): string[] {
  const errors: string[] = [];
  if (!documentationUnlocked(incident)) {
    errors.push("A staff member must confirm the alert before this record can be finalized.");
    return errors;
  }
  if (isRecordLocked(incident) && incident.status !== "in_documentation" && incident.status !== "open") {
    errors.push("This record is already locked.");
    return errors;
  }
  const doc = applyLateFlag(incident.documentation, now);
  const decision = incident.reasonable_grounds_decision?.decision;

  if (!doc.resident_confirmed) errors.push("Confirm the resident.");
  if (!doc.event_time_confirmed) errors.push("Confirm the event time.");
  if (!doc.discovered_at_confirmed) errors.push("Confirm when staff discovered the event.");
  if (!doc.location_confirmed) errors.push("Confirm the location.");
  if (!doc.responders_confirmed) errors.push("Confirm who responded.");
  if (!doc.prefilled_confirmed) errors.push("Confirm that you reviewed each field filled by NeuraVue.");
  requireText(errors, doc.observed, "Write what you observed, in your own words.");
  requireText(errors, doc.persons_present, "Name the people who were present or who discovered it.");
  requireText(errors, doc.care_given, "Record the care given and who gave it.");
  requireText(errors, doc.immediate_prevention, "Record what you did right away to prevent this happening again.");
  requireText(errors, doc.long_term_actions, "Record the longer-term actions planned.");
  requireText(errors, doc.outcome, "Record how the people involved are now.");
  if (!doc.witnessed) errors.push("Record whether staff witnessed the event.");
  if (doc.witnessed === "yes") requireText(errors, doc.witnessed_by, "Name the staff who witnessed it.");
  if (!doc.hospital_transfer) errors.push("Record whether the resident was transferred to hospital.");
  if (doc.hospital_transfer === "yes") requireText(errors, doc.hospital_name, "Name the hospital.");
  if (!decision) {
    errors.push(
      "Record whether you have reasonable grounds to suspect abuse, neglect, improper care, or unlawful conduct."
    );
  }
  if (decision === "yes") {
    requireText(
      errors,
      incident.reasonable_grounds_decision?.suspicion,
      "Write the suspicion and the information it is based on."
    );
  }
  if (decision === "unsure") {
    errors.push("You chose Unsure. Escalate to the nurse in charge before finalizing.");
  }
  if (!doc.critical_incident_category_confirmed) errors.push("Confirm the incident category.");
  if (doc.late_entry && !hasText(doc.late_entry_reason)) {
    errors.push("This entry is late. Add the reason.");
  }
  if (!doc.attestation_accepted) {
    errors.push("Attest that you reviewed this record and it is accurate.");
  }
  requireText(errors, doc.attestation_name, "Enter your name on the attestation.");
  requireText(errors, doc.attestation_designation, "Enter your designation (RN, RPN, NP, or PSW).");

  if (missingNotification(incident, "charge_nurse")) {
    errors.push("Record that the nurse in charge was told, including the time.");
  }

  if (incident.type === "fall") {
    requireText(errors, doc.leading_events, "Record what led up to the fall.");
    requireText(errors, doc.consciousness, "Record level of consciousness and any change from baseline.");
    requireText(errors, doc.vitals, "Record vital signs and the time you took them.");
    requireText(errors, doc.pain, "Record pain: the scale you used and the score.");
    if (!doc.injuries_none) requireText(errors, doc.injuries, "Describe injuries, or choose none observed.");
    if (!doc.post_fall_instrument_confirmed || !hasText(doc.post_fall_instrument)) {
      errors.push("Confirm the post-fall assessment tool name and version.");
    }
    if (!doc.post_fall_completed_at) errors.push("Record when the post-fall assessment was completed.");
    requireText(errors, doc.care_plan_updated, "Record whether the plan of care was updated.");
    if (missingNotification(incident, "physician")) {
      errors.push("Record that the physician or nurse practitioner was told, including the time.");
    }
    if (missingNotification(incident, "sdm_family")) {
      errors.push("Record that the family or substitute decision-maker was told, including the time.");
    }
  }

  if (incident.type === "unsafe_interaction") {
    requireText(errors, doc.leading_events, "Record what led up to the event.");
    requireText(errors, doc.other_residents, "Name other residents involved, or write none.");
    requireText(errors, doc.resident_statement, "Record the resident's own words, or that they could not speak.");
    requireText(errors, doc.consciousness, "Record level of consciousness.");
    requireText(errors, doc.pain, "Record pain.");
    if (!doc.injuries_none) requireText(errors, doc.injuries, "Describe injuries, or choose none observed.");
    requireText(errors, doc.distress, "Record distress you saw, or write none observed.");
    requireText(errors, doc.safety_measures, "Record how you kept the resident safe.");
    if (decision === "yes" && !hasText(doc.investigation_lead)) {
      errors.push("Name the investigation lead.");
    }
    if (missingNotification(incident, "doc")) {
      errors.push("Record that the director of care or administrator was told, including the time.");
    }
    if (doc.sdm_responsible !== "yes" && missingNotification(incident, "sdm_family")) {
      errors.push("Record that the family or substitute decision-maker was told, including the time.");
    }
  }

  if (incident.type === "responsive_behaviour") {
    requireText(errors, doc.leading_events, "Record what led up to the event.");
    requireText(errors, doc.distress, "Record distress you saw.");
    requireText(errors, doc.triggers, "Record triggers, or write none identified.");
    requireText(errors, doc.intervention, "Record the intervention and how the resident responded.");
    requireText(errors, doc.safety_measures, "Record the safety measures.");
    requireText(errors, doc.care_plan_updated, "Record whether the plan of care was updated.");
  }

  if (incident.type === "wandering_zone") {
    if (!doc.missing_last_seen || !doc.missing_found_time || !hasText(doc.missing_found_location)) {
      errors.push("Record when the resident was last seen, when they were found, and where.");
    }
    requireText(errors, doc.leading_events, "Record what led up to the exit, or that the resident was not located.");
    requireText(errors, doc.care_plan_updated, "Record whether the plan of care was updated.");
    if (missingNotification(incident, "sdm_family")) {
      errors.push("Record that the family or substitute decision-maker was told, including the time.");
    }
  }

  if (incident.province === "QC" && (!doc.qc_ah223_confirmed || !doc.qc_ah223_required)) {
    errors.push("Confirm whether an AH-223-1 declaration is required.");
  }

  if (decision === "yes" && doc.criminal_offence === "yes" && missingNotification(incident, "police")) {
    errors.push("Record the police notification, including the occurrence number.");
  }

  return errors;
}
